/**
 * PeopleLayout Component
 *
 * Main layout for People & Companies module.
 * Contains header with counts, tab navigation.
 * Renders ContactsList/CompaniesList directly (like TasksModule pattern).
 * Detail pages render as fixed overlays via Outlet.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { contactsApi, companiesApi } from '@/services/contactsApi';
import ContactsList from './ContactsList';
import CompaniesList from './CompaniesList';

type Tab = 'contacts' | 'companies';

export default function PeopleLayout() {
  const { t } = useTranslation('people');
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active tab from URL
  const isCompaniesTab = location.pathname.includes('/companies');
  const activeTab: Tab = isCompaniesTab ? 'companies' : 'contacts';

  // Counts for header
  const [contactsCount, setContactsCount] = useState(0);
  const [companiesCount, setCompaniesCount] = useState(0);

  // Fetch counts on mount
  const fetchCounts = useCallback(async () => {
    try {
      // Fetch contacts count
      const contactsResponse = await contactsApi.list(1, 1);
      setContactsCount(contactsResponse.total_count);
    } catch (err) {
      console.error('Failed to fetch contacts count:', err);
    }
    try {
      // Fetch companies count
      const companiesResponse = await companiesApi.list();
      setCompaniesCount(companiesResponse.total_count || companiesResponse.companies?.length || 0);
    } catch (err) {
      console.error('Failed to fetch companies count:', err);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const handleTabChange = (tab: Tab) => {
    if (tab === 'contacts') {
      navigate('/people/contacts');
    } else {
      navigate('/people/companies');
    }
  };

  const handleNewClick = () => {
    if (activeTab === 'contacts') {
      navigate('/people/contacts/new');
    } else {
      navigate('/people/companies/new');
    }
  };

  // NOTE: We always render the full layout. Detail pages (ContactDetailPage, CompanyDetailPage)
  // use fixed positioning (fixed inset-0 z-50) to overlay on top of the list.
  // This keeps the list mounted and preserves scroll position when closing detail view.

  return (
    <div className="bg-gray-50" style={{ minHeight: 'calc(100vh - var(--health-banner-height, 0px))' }}>
      {/* Header - uses CSS variable from HealthBanner */}
      <header className="bg-white border-b border-gray-200 sticky z-30" style={{ top: 'var(--health-banner-height, 0px)' }}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Back button */}
              <button
                onClick={() => navigate('/voice')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label={t('layout.back_to_voice')}
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">{t('layout.title')}</h1>
                <p className="text-xs md:text-sm text-gray-500">
                  {t('layout.contacts_count', { count: contactsCount })} {companiesCount} {t('layout.tabs.companies').toLowerCase()}
                </p>
              </div>
            </div>
            <button
              onClick={handleNewClick}
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {activeTab === 'contacts' ? t('layout.new_contact') : t('layout.new_company')}
            </button>
          </div>

          {/* Tab navigation */}
          <div className="mt-4 flex gap-1 -mb-px">
            <button
              onClick={() => handleTabChange('contacts')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'contacts'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('layout.tabs.contacts')}
              {contactsCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                  {contactsCount}
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabChange('companies')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'companies'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('layout.tabs.companies')}
              {companiesCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                  {companiesCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main content - render list directly (like TasksModule pattern) */}
      <main className="max-w-7xl mx-auto px-6 py-6">
        {activeTab === 'contacts' ? (
          <ContactsList onCountChange={setContactsCount} />
        ) : (
          <CompaniesList onCountChange={setCompaniesCount} />
        )}
      </main>

      {/* Outlet for overlays: detail pages, create forms */}
      <Outlet context={{ refreshCounts: fetchCounts }} />
    </div>
  );
}

// Hook to access layout context
export function usePeopleLayoutContext() {
  return { refreshCounts: () => {} };
}
